import { useState, useEffect, forwardRef, PropsWithRef } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { Button } from '@xanectech/ui/components';
import { useEncryptionStatus, useAuth } from '@xanectech/auth';
import { createLogger } from '@xanectech/lib';
const logger = createLogger('TrueID.Locker - UnlockStatusButton');
import { useUnlockDialog } from '../contexts/UnlockDialogContext';

/**
 * Interactive unlock status button component
 *
 * Shows current lock/unlock status and allows user to:
 * - Lock their data (requires re-unlock to access)
 * - Unlock their data (opens unlock dialog)
 * - See remaining time on time-limited unlocks
 */
export const UnlockStatusButton = () => {
  const { isUnlocked, markLocked, refresh } = useEncryptionStatus();
  const { user } = useAuth();
  const [isLocking, setIsLocking] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);
  const { openDialog } = useUnlockDialog();

  // Refresh encryption status when user changes (login/logout)
  useEffect(() => {
    if (user) {
      void refresh();
    }
  }, [user, refresh]);

  // Calculate and display time remaining until unlock expires
  useEffect(() => {
    if (!isUnlocked) {
      setTimeRemaining(null);
      return;
    }

    // Fetch unlock expiry from IndexedDB
    const checkExpiry = async () => {
      try {
        type UnlockStatusRecord = {
          expiresAt: number | null;
        };

        const db = await new Promise<IDBDatabase>((resolve, reject) => {
          const request = indexedDB.open('XanecTechUnlockStatus', 1);
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(new Error('Failed to open DB'));
        });

        const transaction = db.transaction(['unlock_status'], 'readonly');
        const store = transaction.objectStore('unlock_status');
        const request = store.getAll();

        request.onsuccess = () => {
          const records = request.result as UnlockStatusRecord[];
          const firstRecord = records[0];

          if (firstRecord) {
            const expiresAt = firstRecord.expiresAt;

            // Handle "never expire" case (expiresAt is null)
            if (expiresAt === null) {
              setTimeRemaining('∞'); // Infinity symbol for never expire
              db.close();
              return;
            }

            const now = Date.now();
            const remaining = Math.max(0, expiresAt - now);

            // Format time remaining
            if (remaining > 0) {
              const hours = Math.floor(remaining / (1000 * 60 * 60));
              const minutes = Math.floor(
                (remaining % (1000 * 60 * 60)) / (1000 * 60),
              );
              const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

              if (hours > 0) {
                setTimeRemaining(`${hours}h ${minutes}m`);
              } else if (minutes > 0) {
                setTimeRemaining(`${minutes}m ${seconds}s`);
              } else {
                setTimeRemaining(`${seconds}s`);
              }
            } else {
              setTimeRemaining(null);
            }
          }
          db.close();
        };
      } catch (error) {
        // Silently fail - just don't show timer
        logger.error('Failed to check unlock expiry', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void checkExpiry();
    const interval = setInterval(() => void checkExpiry(), 1000); // Update every second
    return () => clearInterval(interval);
  }, [isUnlocked]);

  const handleLock = async () => {
    setIsLocking(true);
    try {
      await markLocked();
    } catch (error) {
      logger.error('Failed to lock session', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsLocking(false);
    }
  };

  const handleUnlock = () => {
    openDialog();
  };

  return (
    <div className='flex items-center gap-2'>
      {isUnlocked ? (
        <>
          <div className='flex items-center gap-2 px-3 py-1 bg-success/10 dark:bg-success/5 text-success rounded-full text-sm'>
            <Unlock className='w-4 h-4' />
            <span className='font-medium'>Unlocked</span>
            {timeRemaining && (
              <span className='text-xs opacity-75'>({timeRemaining})</span>
            )}
          </div>
          <Button
            variant='ghost'
            size='sm'
            onClick={handleLock}
            disabled={isLocking}
            title='Lock your data (requires unlock to access)'
            className='h-8 px-2'
          >
            <Lock className='w-4 h-4' />
          </Button>
        </>
      ) : (
        <Button
          variant='outline'
          size='sm'
          onClick={handleUnlock}
          title='Unlock your data with biometric authentication'
          className='flex items-center gap-2'
        >
          <Lock className='w-4 h-4' />
          Locked
        </Button>
      )}
    </div>
  );
};
